import { Component, DestroyRef, ElementRef, inject, Inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '../../models/user';
import { UsersService } from '../../services/user.service';
import { canCreateProjectInEffort, canEditEffortProjects } from '../../project-management/project-permissions';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormControlStatus, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { Effort } from '../../models/effort';
import { Project, ProjectCustomValue, ProjectTimeType, TimeType } from '../../models/project';
import { EffortChooserComponent } from '../effort-chooser/effort-chooser.component';
import { EffortsService } from '../../services/efforts.service';
import { FilterChip } from '../../models/filter-chip';
import { SlugInputDirective } from '../../directives/slug-input.directive';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { PrioritizedChipGridComponent } from '../../components/prioritized-chip-grid/prioritized-chip-grid.component';
import { Subject, BehaviorSubject, Subscription, tap, debounce, debounceTime, Observable, combineLatest, switchMap, takeUntil } from 'rxjs';
import { TimeTypesService } from '../../services/time-types.service';
import { MatTabsModule } from '@angular/material/tabs';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { MFKValidator } from './mfk.validator';

@Component({
  selector: 'app-project-dialog',
  templateUrl: './project-editor.component.html',
  styleUrls: ['./project-editor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTableModule,
    MatIconModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatSelectModule,
    SlugInputDirective,
    MatCheckboxModule,
    MatTabsModule,
    DragDropModule
  ],
})
export class ProjectEditorComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly usersService = inject(UsersService);
  private currentUser?: User;
  @ViewChild("newKeyInput") newKeyInput!: ElementRef;
  projectForm: FormGroup;
  originalValues: any;
  effort?: Effort;
  filters: FilterChip[] = [];
  selectedFilters: Subject<FilterChip[]> = new BehaviorSubject<FilterChip[]>([]);
  timeTypes: FilterChip[] = [];
  timeTypesService = inject(TimeTypesService);
  http = inject(HttpClient);
  ttsubscription: Subscription;
  customValues = new Map<string, string>();
  keyNew?: string;
  valueNew?: string;
  displayedKeyColumns: string[] = ['key'];
  displayedValueColumns: string[] = ['value'];
  mfkValidator = inject(MFKValidator);
  mfkError = signal('');
  selectedEffort?: Effort;
  readonly oacctValidators = Validators.compose([Validators.pattern(/\d{3}/), Validators.min(1), Validators.required]);

  constructor(
    public dialogRef: MatDialogRef<ProjectEditorComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: { project: Project, suggestedEffort: FilterChip },
    private fb: FormBuilder,
    private effortService: EffortsService
  ) {
    this.projectForm = this.fb.group({
      id: [ data.project?.id],
      key: [data.project?.key || '', Validators.required],
      title: [data.project?.title || '', Validators.required],
      owner: [data.project?.owner || ''],
      description: [data.project?.description || ''],
      customer: [data.project?.customer || ''],
      oAccount: [data.project?.oAccount || '', this.oacctValidators ],
      mfk: [ {value: data.project?.mfk || '', disabled: true }, [], [ this.mfkValidator ] ],
      hasNonbillable: [ { value: !!data.project?.projectTimeTypes?.find(ptt => ptt.timeTypeId == 2), disabled: true } ],
      effortId: ['', Validators.required],
      isDeleted: [ data.project?.isDeleted ],
      isBillable: [ !!data.project?.projectTimeTypes?.find(ptt => ptt.timeTypeId == 1) ],
      projectTimeTypes: [ data.project?.projectTimeTypes ],
      allowInvalidMFK: [ false ],
      //effortKey: [data.project?.effort.key || '', Validators.required],
      selectedEffort: ['[None selected]', Validators.required]
    });
    this.originalValues = this.projectForm.value;
    this.projectForm.valueChanges.pipe(debounceTime(50))
    .subscribe(_ => {
      const billable = this.projectForm.get('isBillable')?.value;
        if (billable) {
          this.projectForm.get('mfk')!.enable({emitEvent: false});
          this.projectForm.get('hasNonbillable')!.enable({emitEvent: false});
        } else {
          this.projectForm.get('mfk')!.disable({emitEvent: false});
          this.projectForm.get('hasNonbillable')!.disable({emitEvent: false});
        }
    });
    this.ttsubscription = this.timeTypesService
      .fetch()
      // .pipe(tap(tt => console.log(tt)))
      .subscribe(tt => {
        this.timeTypes = tt.map(t => <FilterChip>{ chip: t.name, value: ''+t.id });
        this.selectedFilters.next(this.timeTypes.filter(tt => data.project?.projectTimeTypes.find(ptt => ptt.timeTypeId == parseInt(tt.value))));
      });
  }

  ngOnInit() {
    this.usersService.getCurrentUser().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.currentUser = user;
      this.selectInitialEffort();
    });
    this.setupCustomValues();
  }

  selectInitialEffort() {
    if (this.data.suggestedEffort) {
      this.effortService
        .filterEfforts([{ chip: 'key', value: this.data.suggestedEffort.value }])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(e => {
          console.log("Would select effort.", e);
          if (e.total === 1) {
            this.selectEffort(e.efforts[0]);
          }
        });
    } else if (this.data.project?.effort) {
      this.selectEffort(this.data.project.effort);
    }   
  }
  
  setupOAcctValidators() {
    const oacct = this.projectForm.get('oAccount');
    console.log(this.selectedEffort?.principalGroup.key.toLowerCase());
    oacct?.clearValidators();
    if (this.selectedEffort && this.selectedEffort.principalGroup.key.toLowerCase().startsWith('its') && this.oacctValidators) {
      oacct?.addValidators(this.oacctValidators);
    }
    oacct?.updateValueAndValidity();
  }

  setupCustomValues() {
    this.customValues.clear();
    this.data.project?.projectCustomValues?.map(pcv => this.customValues.set(pcv.key, pcv.value));
  }

  ngAfterViewInit() {
    this.projectForm.get('selectedEffort')?.disable();
  }

  ngOnDestroy() {
    this.ttsubscription.unsubscribe();
  }

  mfkParts = [3,2,4,5,8,4,3,5,2,4];
  mfkErrorMap = [
    ['FUND DOES NOT EXIST', 'FUND'],
    ['WHO DOES NOT EXIST', 'WHO', 'ORG UNIT'],
    ['DEPT/SUBDEPT DOES NOT EXIST', 'DEPT/SUBDEPT'],
    ['DEPT/SUBDEPT DOES NOT EXIST', 'DEPT/SUBDEPT'],
    ['GRANT/PROGRAM DOES NOT EXIST', 'GRANT/PROGRAM INVALID', 'GRANT/PROGRAM'],
    ['ACCOUNT DOES NOT EXIST', 'BUDGET ONLY ACCOUNT', 'ACCOUNT'],
    undefined,
    undefined,
    ['FUNCTION DOES NOT EXIST', 'FUNCTION'],
    ['COST CENTER INVALID', 'COST CENTER'],
  ];
  mfkPartNames = 'FND OG DEPT SDEPT GRANTPRG IACC OAC DACCT FN CCTR'.split(' ');
  mfkHelpTexts = "Fund,Org,Department,Subdepartment,Grant/Program,IAccount,OAccount,DAccount,Function,Cost Center".split(',');
  // getMFKErrorHighlightIndex(message: string) {
  //   const k = Object.keys(this.mfkErrorMap).find(k => message.toUpperCase().includes(k))
  //   if (k) {
  //     let parts = '';
  //     let targets = this.mfkErrorMap[k];
  //     for (let i=0; i<this.mfkParts.length; i++) {
        
  //     }
  //   }
  //    ? this.mfkErrorMap[k] : 
  // }

  mfkErrorInPart(i: number, e: string) {
    return this.mfkErrorMap[i] !== undefined && e && this.mfkErrorMap[i].find(m => e.includes(m));
  }

  formatMFKWithSpaces() {
    //260 43 5071 71000 00000000 6215 452 10785 00 0000
    const raw = this.projectForm.get('mfk')?.value;
    const nocorp = raw.match(/^10\s+/) ? raw.substring(2).trim() : raw;
    const nospaces = nocorp.split(/[\s-]+/).join('');
    const [,spaces] = this.mfkParts.reduce(([remaining, parts], len) => {
      const l = Math.min(remaining.length, len);
      parts.push(remaining ? remaining.substring(0, l) : '');
      remaining = remaining.substring(l);
      return [remaining, parts];
    }, [nospaces, <string[]>[]]);
    this.projectForm.get('mfk')?.setValue(spaces.join(' ').trim());
  }

  save(): void {
    if (this.projectForm.valid && !this.projectForm.pending && this.canSave) {
      const proj = <Project>this.projectForm.value;
      (<any>proj).isBillable = undefined;
      if (!proj.id) proj.id = undefined;
      proj.isDeleted = !!proj.isDeleted;
      proj.projectTimeTypes = [];
      if (this.projectForm.get('isBillable')?.value) {
        proj.projectTimeTypes.push(<ProjectTimeType>{ projectId: proj.id ?? undefined, timeTypeId: 1 })
        if (this.projectForm.get('hasNonbillable')?.value) {
          proj.projectTimeTypes.push(<ProjectTimeType>{ projectId: proj.id ?? undefined, timeTypeId: 2 })
        }
      }
      proj.projectCustomValues = structuredClone(this.data.project?.projectCustomValues ?? []);
      if (this.keyNew?.trim()) {
        this.addCustomValue();
      }
      for (let [key, value] of this.customValues.entries()) {
        let cv = proj.projectCustomValues.find(c => c.key == key);
        if (!cv) {
          cv = <ProjectCustomValue>{ projectId: proj.id };
          console.info("Adding", cv);
          proj.projectCustomValues.push(cv)
        }
        cv.key = key;
        cv.value = value;
      }
      for (let pcv of proj.projectCustomValues) {
        if (!this.customValues.has(pcv.key)) {
          proj.projectCustomValues = proj.projectCustomValues.filter(p => p.key != pcv.key);
        }
      }
      this.dialogRef.close(proj);
    }
  }

  keyify(event: Event) {
    const keyref = this.projectForm.get('key');
    if (keyref?.value?.toString().trim()) {
      return;
    }

    const keyified = this.projectForm.get('title')?.value?.replace(/\s+/g, '-').toLowerCase() ?? null;
    if (keyified)
      keyref?.setValue(keyified);
  }

  openEffortSelectDialog(): void {
    const dialogRef = this.dialog.open(EffortChooserComponent, {
      width: '500px',
      maxHeight: '50vh',
      data: { canSelect: (effort: Effort) => this.canSelectEffort(effort) },
      // data: { ... } // if you need to pass data in
    });

    dialogRef.afterClosed().subscribe((selectedEffort: Effort) => {
      if (selectedEffort) {
        console.info(this.openEffortSelectDialog.name, 'User selected:', selectedEffort.id, selectedEffort);
        this.selectEffort(selectedEffort);
      } else {
        console.info(this.openEffortSelectDialog.name, 'Dialog was closed without selection');
      }
    });
  }

  removeCustomValue(key: string) {
    this.customValues.delete(key);
  }

  addCustomValue() {
    const newKey = this.keyNew?.trim();
    if (!newKey) return;
    this.customValues.set(this.keyNew!, this.valueNew!);
    this.valueNew = this.keyNew = '';
    this.newKeyInput.nativeElement.focus();
  }

  reset() {
    this.projectForm.reset(this.originalValues);
    this.selectInitialEffort();
    this.setupCustomValues();
  }

  selectEffort(effort: Effort) {
    if (!this.canSelectEffort(effort)) return;
    this.selectedEffort = effort;
    this.projectForm.get('effortId')?.setValue(effort.id);
    this.projectForm.get('selectedEffort')?.setValue(`${effort.title}`);
    this.setupOAcctValidators();
  }

  canSelectEffort(effort: Effort): boolean {
    return this.data.project?.id
      ? canEditEffortProjects(this.currentUser, effort)
      : canCreateProjectInEffort(this.currentUser, effort);
  }

  get canSave(): boolean {
    if (!this.selectedEffort || !this.canSelectEffort(this.selectedEffort)) return false;
    return !this.data.project?.id || canEditEffortProjects(this.currentUser, this.data.project.effort);
  }

  filterChange(filters: FilterChip[]): void {
    console.log("Filter changed", filters);
    const tt = [];
    for (let f of filters) {
      const existing = this.data.project.projectTimeTypes.find(ptt => ptt.timeTypeId == parseInt(f.value));
      tt.push(existing ?? <ProjectTimeType>{ timeTypeId: parseInt(f.value), projectId: this.data.project.id });
    }
    this.projectForm.get('projectTimeTypes')?.setValue(tt);
    // this.filterUtility.updateUrlWithFilters(filters, '/efforts');
    // this._loading.next(true);
    // this._dataChangedSubject.next(filters);
}
}


